import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
  VStack,
  Text,
  Heading,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { EmailIcon, LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

// Memoized AuthForm to prevent re-renders affecting input fields
const AuthForm = memo(({
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  buttonText,
  onSubmit,
  isLoading // Add loading state prop
}: {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  password: string;
  setPassword: React.Dispatch<React.SetStateAction<string>>;
  showPassword: boolean;
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
  buttonText: string;
  onSubmit: () => void;
  isLoading: boolean; // Add loading state prop type
}) => {
  return (
    <VStack spacing={4}>
      <FormControl>
        <FormLabel>Email</FormLabel>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <EmailIcon color="gray.500" />
          </InputLeftElement>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            _placeholder={{ color: 'gray.500' }}
            borderRadius="lg"
          />
        </InputGroup>
      </FormControl>
      <FormControl>
        <FormLabel>Password</FormLabel>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <LockIcon color="gray.500" />
          </InputLeftElement>
          <Input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            _placeholder={{ color: 'gray.500' }}
            borderRadius="lg"
          />
          <InputRightElement>
            <Icon
              as={showPassword ? ViewOffIcon : ViewIcon}
              cursor="pointer"
              onClick={() => setShowPassword(!showPassword)}
              color="gray.500"
            />
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <Button
        colorScheme="blue"
        onClick={onSubmit}
        width="100%"
        size="lg"
        borderRadius="lg"
        bgGradient="linear(to-r, blue.400, purple.500)"
        _hover={{
          bgGradient: "linear(to-r, blue.500, purple.600)",
          transform: "translateY(-2px)",
          boxShadow: "lg",
        }}
        transition="all 0.2s"
        isLoading={isLoading} // Show loader when loading
        loadingText="Submitting" // Optional: customize loading text
      >
        {buttonText}
      </Button>
    </VStack>
  );
});
AuthForm.displayName = 'AuthForm';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const navigate = useNavigate();
  const toast = useToast();
  const auth = getAuth();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleAuth = async (isLogin: boolean) => {
    setIsLoading(true); // Start loading
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/dashboard');
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  return (
    <Flex minH="100vh" align="center" bg={useColorModeValue('gray.50', 'gray.900')}>
      <Container maxW="container.xl" py={12}>
        <Stack direction={{ base: 'column', lg: 'row' }} spacing={8} align="center">
          {/* Left side - Branding/Logo */}
          <MotionBox
            flex={1}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            textAlign={{ base: 'center', lg: 'left' }}
          >
            <Heading
              fontSize={{ base: "4xl", lg: "5xl" }}
              bgGradient="linear(to-r, blue.400, purple.500)"
              bgClip="text"
              mb={4}
            >
              Welcome to Finomo
            </Heading>
            <Text fontSize="xl" color="gray.500" maxW="lg">
              Your comprehensive financial market analysis platform. Start your journey to smarter investing today.
            </Text>
          </MotionBox>

          {/* Right side - Auth Form */}
          <MotionBox
            flex={1}
            w={{ base: "full", lg: "auto" }}
            maxW={{ base: "md", lg: "500px" }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              bg={bgColor}
              borderRadius="2xl"
              boxShadow="xl"
              p={8}
              border="1px"
              borderColor={borderColor}
            >
              <Tabs isFitted variant="soft-rounded" colorScheme="blue">
                <TabList mb={4}>
                  <Tab _selected={{ bg: 'blue.100', color: 'blue.800' }}>Login</Tab>
                  <Tab _selected={{ bg: 'blue.100', color: 'blue.800' }}>Sign Up</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <AuthForm 
                      email={email}
                      setEmail={setEmail}
                      password={password}
                      setPassword={setPassword}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      buttonText="Login" 
                      onSubmit={() => handleAuth(true)} 
                      isLoading={isLoading} // Pass loading state
                    />
                  </TabPanel>
                  <TabPanel>
                    <AuthForm 
                      email={email}
                      setEmail={setEmail}
                      password={password}
                      setPassword={setPassword}
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      buttonText="Sign Up" 
                      onSubmit={() => handleAuth(false)} 
                      isLoading={isLoading} // Pass loading state
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </MotionBox>
        </Stack>
      </Container>
    </Flex>
  );
};
